// @ts-nocheck
import { resolveColor, safeColor } from "@insite/mobius/utilities";
import { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Props";
// import { buttonStyles } from "@store/Themes/common/mixins/button"; // Store Custom Code
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { ButtonType, MobiusButtonType } from "@toolkit/Framework/Types/Button";
import Styleguide from "@toolkit/Framework/Types/Styleguide";
import { buttonGetHoverProps } from "@toolkit/Themes/common/props";
import { css } from "styled-components";

/* Store Custom Code - Start */
import getColor from "@insite/mobius/utilities/getColor";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { buttonStyles } from "@store/Themes/common/mixins/button";
/* Store Custom Code - End */

/* ------------------------------------------------------------------------------------------ */
/* Related Extensions => src/Extensions/Mobius/Button --------------------------------------- */
/* ------------------------------------------------------------------------------------------ */

interface ButtonOptions {
    color: string;
    type: ButtonType;
    hoverColor?: string;
    hoverTextColor?: string;
    // Store Custom Code
    isAltText?: boolean;
}

const getButton = (options: ButtonOptions): ButtonPresentationProps => ({
    shape: "rounded",
    buttonType: options.type as MobiusButtonType,
    typographyProps: { weight: "inherit" },
    color: options.color,
    css: buttonStyles(options.type, options.isAltText),
    ...buttonGetHoverProps(options.hoverColor || `${options.color}.dark`),
    hoverTextColor: options.hoverTextColor,
});

interface SelectionButtonOptions {
    color: string;
    hoverColor: string;
    textColor?: string;
    showBackground?: boolean;
    // Store Custom Code
    hoverTextColor?: string;
}

const getSelectionButton = (options: SelectionButtonOptions = { color: "", hoverColor: "", showBackground: true }) =>
    mergeProps<ButtonPresentationProps>(
        getButton({
            color: options.color,
            type: "outline",
            hoverColor: options.hoverColor,
            hoverTextColor: options.textColor, // Store Custom Code
        }),
        {
            css: css`
                max-width: 100%;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                /* Store Custom Code - Start */
                position: relative;
                min-width: 0;
                border-width: 1px;
                border-radius: 4px;
                font-weight: ${x => x.theme.design.typography.weight.semibold};
                font-size: ${x => x.theme.design.typography.size.smaller};
                text-transform: none;
                letter-spacing: 0;

                &::before {
                    content: "";
                    position: absolute;
                    top: -1px;
                    right: -1px;
                    bottom: -1px;
                    left: -1px;
                    border-width: 1.5px;
                    border-style: inherit;
                    border-color: inherit;
                    border-radius: inherit;
                    transition: ${transitionHoverStyles("border-color")};
                }
                /* Store Custom Code - End */

                ${({ theme }) => {
                    return css`
                        ${cssProp("color", options.textColor, x => resolveColor(x, theme))}
                        ${options.showBackground
                            ? `background-color: ${safeColor(resolveColor(options.color, theme)).alpha(0.1)};`
                            : null}
                        /* Store Removed - font-weight */

                        &:disabled {
                            /* Store Removed - background-color */
                        }
                    `;
                }};
            `,
        },
    );

const button: Styleguide["button"] = {
    primary: getButton({ color: "primary", type: "solid" }),
    /* Store Custom Code - Start */
    secondary: getButton({ color: "primary", type: "mixed", isAltText: true }),
    tertiary: mergeProps<ButtonPresentationProps>(getButton({ color: "primary", type: "outline", isAltText: true }), {
        css: css`
            border-color: ${getColor("common.border")};
        `,
    }),
    inverted: getButton({ color: "invert", type: "solid", hoverTextColor: "invert.hover", isAltText: true }),
    invertedOutline: getButton({ color: "invert", type: "mixed", isAltText: true }),
    /* Store Custom Code - End */
    selected: getSelectionButton({ color: "swatch.selected", hoverColor: "swatch.selectedHover" }),
    /* Store Custom Code - Start */
    unselected: mergeProps<ButtonPresentationProps>(
        getSelectionButton({
            color: "swatch",
            hoverColor: "swatch.hover",
            textColor: "swatch.contrast",
            showBackground: false,
        }),
        {
            css: css`
                color: ${getColor("text.accent")};
                font-weight: ${x => x.theme.design.typography.weight.base};

                &:not(:disabled).unavailable {
                    background-color: transparent;
                    text-decoration: line-through;
                    border-style: dashed;
                    color: ${getColor("text.disabledLight")};
                }

                &::before {
                    border-color: transparent;
                }
            `,
        },
    ),
    /* Store Custom Code - End */
};

export default button;
